import { c as _c } from "react/compiler-runtime";
import type { L10n } from '@onefootprint/footprint-js-legacy';
import { Idv2, Idv2Provider, LoadNeuroId } from '@onefootprint/idv2';
import { getErrorMessage } from '@onefootprint/request';
import type { IdDocOutcome, OverallOutcome } from '@onefootprint/types';
import { SessionStatus } from '@onefootprint/types';
import { type ComponentProps, useEffect, useMemo } from 'react';
import { type DoneArgs, Identify } from '@/idv/components/identify';
import { IdentifyVariant } from '@/idv/components/identify/identify.types';
import GenericErrorPage from '../../../src/components/gerenic-error-page/generic-error-page';
import { IDV_SESSION_RETRY_LIMIT } from '../../../src/config/constants';
import { AppErrorBoundary, SessionExpired, useLayoutOptions } from '../../components';
import { L10nContextProvider } from '../../components/l10n-provider';
import { useIdvMachine, useLogStateMachine } from '../../hooks';
import { useValidateSession } from '../../queries';
import { convertBootstrapToVaultData, FPCustomEvents, getLogger, trackAction } from '../../utils';
import { createAuthTokenChangedPayload, createReceivedDeviceResponseJsonPayload } from '../../utils/state-machine/utils/custom-listener';
import ConfigInvalid from '../config-invalid';
import Init from '../init';
import Onboarding from '../onboarding';
type RouterProps = {
  l10n?: L10n;
  onIdentifyDone?: ({
    authToken
  }: {
    authToken: string;
  }) => void;
};
const {
  receivedDeviceResponseJson,
  stepUpCompleted
} = FPCustomEvents;
const {
  logInfo,
  logWarn
} = getLogger({
  location: 'idv-router'
});

/**
 * Renders inside Idv2Provider.
 * During sandbox-outcome: renders <Idv2 initialPath="/sandbox-outcome">.
 * During onboarding: renders <Onboarding>. The inner router
 * (onboarding/pages/router/router.tsx) handles relayToIDV2 and renders
 * <Idv2> as a child when true — keeping xstate alive across relay toggles.
 */
const Idv2OrLegacy = t0 => {
  const $ = _c(3);
  const {
    isSandboxOutcome,
    onboardingProps
  } = t0;
  if (isSandboxOutcome) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Idv2 initialPath="/sandbox-outcome" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== onboardingProps) {
    t1 = <Onboarding {...onboardingProps} />;
    $[1] = onboardingProps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const Router = t0 => {
  const $ = _c(82);
  const {
    l10n,
    onIdentifyDone
  } = t0;
  const [state, send, service] = useIdvMachine();
  useLogStateMachine("idv", state);
  const {
    options: layoutOptions
  } = useLayoutOptions();
  const {
    config,
    device,
    authToken,
    bootstrapData,
    isTransfer,
    componentsSdkContext,
    isInIframe,
    obConfigAuth,
    idDocOutcome,
    overallOutcome,
    onClose,
    onComplete,
    autoAdvance,
    onBackButtonVisibilityChange,
    onRequirementChange,
    onExternalBack,
    retries,
    showLogo,
    sandboxId,
    fixtureRiskSignals
  } = state.context;
  const isIdv2Ready = !!config && !!device;
  const retryLimitExceeded = retries > IDV_SESSION_RETRY_LIMIT;
  let t1;
  if ($[0] !== state) {
    t1 = state.matches("identify");
    $[0] = state;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isIdentifying = t1;
  let t2;
  let t3;
  if ($[2] !== isIdentifying) {
    t2 = () => {
      if (isIdentifying) {
        logInfo("Identify started", {
          engine: "idv"
        });
      }
    };
    t3 = [isIdentifying];
    $[2] = isIdentifying;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== bootstrapData) {
    t4 = convertBootstrapToVaultData(bootstrapData);
    $[5] = bootstrapData;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    kycData: kycBootstrapData,
    kybData: kybBootstrapData
  } = t4;
  let t5;
  if ($[7] !== authToken) {
    t5 = {
      authToken
    };
    $[7] = authToken;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== send) {
    t6 = {
      onSuccess: sessionStatus => {
        if (sessionStatus !== SessionStatus.active) {
          send({
            type: "expireSession"
          });
          trackAction("expired:check_session", {
            sessionStatus
          });
          logWarn(`Session status: ${sessionStatus}`);
        }
      },
      onError: _temp
    };
    $[9] = send;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useValidateSession(t5, t6);
  let t7;
  if ($[11] !== send) {
    t7 = () => {
      const authTokenListener = e => {
        const payload = createAuthTokenChangedPayload(e);
        if (payload) {
          send(payload);
        }
      };
      const deviceResponseJsonListener = e_0 => {
        const payload_0 = createReceivedDeviceResponseJsonPayload(e_0);
        if (payload_0) {
          send(payload_0);
        }
      };
      document.addEventListener(stepUpCompleted, authTokenListener);
      document.addEventListener(receivedDeviceResponseJson, deviceResponseJsonListener);
      return function cleanup() {
        document.removeEventListener(stepUpCompleted, authTokenListener);
        document.removeEventListener(receivedDeviceResponseJson, deviceResponseJsonListener);
      };
    };
    $[11] = send;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t9;
  if ($[14] !== onIdentifyDone || $[15] !== send) {
    t9 = payload_1 => {
      send({
        type: "identifyCompleted",
        payload: payload_1
      });
      if (onIdentifyDone && payload_1) {
        onIdentifyDone(payload_1);
      }
      trackAction("identify:completed");
      logInfo("Identify completed", {
        engine: "idv"
      });
    };
    $[14] = onIdentifyDone;
    $[15] = send;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleIdentifyDone = t9;
  let t10;
  if ($[17] !== send) {
    t10 = () => send({
      type: "reset"
    });
    $[17] = send;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== config || $[20] !== isTransfer) {
    t11 = !isTransfer && <LoadNeuroId config={config} />;
    $[19] = config;
    $[20] = isTransfer;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== state) {
    t12 = state.matches("init") ? <Init /> : null;
    $[22] = state;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== authToken || $[25] !== bootstrapData || $[26] !== componentsSdkContext || $[27] !== config || $[28] !== device || $[29] !== handleIdentifyDone || $[30] !== l10n || $[31] !== obConfigAuth || $[32] !== onBackButtonVisibilityChange || $[33] !== onExternalBack || $[34] !== overallOutcome || $[35] !== sandboxId || $[36] !== showLogo || $[37] !== state) {
    t13 = state.matches("identify") && config && device ? <L10nContextProvider l10n={l10n}><Identify initArgs={{
        variant: IdentifyVariant.verify,
        device,
        config,
        isLive: config.isLive,
        overallOutcome,
        sandboxId,
        initialAuthToken: authToken,
        obConfigAuth,
        isComponentsSdk: !!componentsSdkContext,
        bootstrapData,
        logoConfig: showLogo ? {
          orgName: config.orgName,
          logoUrl: config.logoUrl || undefined
        } : undefined
      }} onDone={handleIdentifyDone} onBackButtonVisibilityChange={onBackButtonVisibilityChange} onExternalBack={onExternalBack} /></L10nContextProvider> : null;
    $[24] = authToken;
    $[25] = bootstrapData;
    $[26] = componentsSdkContext;
    $[27] = config;
    $[28] = device;
    $[29] = handleIdentifyDone;
    $[30] = l10n;
    $[31] = obConfigAuth;
    $[32] = onBackButtonVisibilityChange;
    $[33] = onExternalBack;
    $[34] = overallOutcome;
    $[35] = sandboxId;
    $[36] = showLogo;
    $[37] = state;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== authToken || $[40] !== autoAdvance || $[41] !== componentsSdkContext || $[42] !== config || $[43] !== device || $[44] !== fixtureRiskSignals || $[45] !== idDocOutcome || $[46] !== isIdv2Ready || $[47] !== isInIframe || $[48] !== isTransfer || $[49] !== kybBootstrapData || $[50] !== kycBootstrapData || $[51] !== l10n?.language || $[52] !== l10n?.locale || $[53] !== layoutOptions?.hideHeaderButton || $[54] !== obConfigAuth || $[55] !== onBackButtonVisibilityChange || $[56] !== onClose || $[57] !== onComplete || $[58] !== onRequirementChange || $[59] !== overallOutcome || $[60] !== sandboxId || $[61] !== send || $[62] !== service || $[63] !== state) {
    t14 = (state.matches("sandboxOutcome") || state.matches("onboarding")) && isIdv2Ready ? <Idv2Provider initialValues={{
      onboardingSessionToken: authToken ?? "",
      config,
      overallOutcome,
      fixtureRiskSignals,
      idDocOutcome,
      l10n: {
        locale: l10n?.locale,
        language: l10n?.language ?? "en"
      },
      obConfigAuth: state.matches("sandboxOutcome") ? obConfigAuth : undefined,
      kycBootstrapData,
      kybBootstrapData,
      isTransfer,
      sandboxId,
      isComponentsSdk: !!componentsSdkContext
    }} onComplete={(validationToken, options) => {
      if (!options?.hasCompletionScreen) {
        send({
          type: "onboardingCompleted",
          payload: {
            validationToken
          }
        });
      }
      trackAction("onboarding:completed");
      onComplete?.({
        validationToken,
        authToken,
        deviceResponseJson: service.getSnapshot().context.deviceResponseJson,
        hasCompletionScreen: options?.hasCompletionScreen
      });
    }} onHandoffComplete={() => {
      onComplete?.({});
    }} onSandboxOutcomeSubmit={payload_2 => send({
      type: "sandboxOutcomeSubmitted",
      payload: {
        overallOutcome: payload_2.overallOutcome as OverallOutcome | undefined,
        fixtureRiskSignals: payload_2.fixtureRiskSignals,
        idDocOutcome: payload_2.idDocOutcome as IdDocOutcome | undefined,
        sandboxId: payload_2.sandboxId
      }
    })} onClose={onClose} autoAdvance={autoAdvance} onBackButtonVisibilityChange={onBackButtonVisibilityChange} onRequirementChange={onRequirementChange} hideNavigationButtons={layoutOptions?.hideHeaderButton} componentsSdkRelay={componentsSdkContext && !componentsSdkContext.skipRelayToComponents ? {
      relayToComponents: componentsSdkContext.relayToComponents,
      onRelayFromComponents: componentsSdkContext.onRelayFromComponents
    } : undefined}><Idv2OrLegacy isSandboxOutcome={state.matches("sandboxOutcome")} onboardingProps={{
        config,
        idvContext: {
          device,
          authToken: authToken ?? "",
          isTransfer,
          componentsSdkContext,
          isInIframe
        },
        overallOutcome,
        fixtureRiskSignals,
        idDocOutcome: idDocOutcome as IdDocOutcome | undefined,
        onClose
      }} /></Idv2Provider> : null;
    $[39] = authToken;
    $[40] = autoAdvance;
    $[41] = componentsSdkContext;
    $[42] = config;
    $[43] = device;
    $[44] = fixtureRiskSignals;
    $[45] = idDocOutcome;
    $[46] = isIdv2Ready;
    $[47] = isInIframe;
    $[48] = isTransfer;
    $[49] = kybBootstrapData;
    $[50] = kycBootstrapData;
    $[51] = l10n?.language;
    $[52] = l10n?.locale;
    $[53] = layoutOptions?.hideHeaderButton;
    $[54] = obConfigAuth;
    $[55] = onBackButtonVisibilityChange;
    $[56] = onClose;
    $[57] = onComplete;
    $[58] = onRequirementChange;
    $[59] = overallOutcome;
    $[60] = sandboxId;
    $[61] = send;
    $[62] = service;
    $[63] = state;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== state) {
    t15 = state.matches("sessionExpired") ? <SessionExpired /> : null;
    $[65] = state;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== retryLimitExceeded || $[68] !== send || $[69] !== state) {
    t16 = state.matches("initConfigFailed") ? <GenericErrorPage onRetry={() => send({
      type: "reset"
    })} retryLimitExceeded={retryLimitExceeded} /> : null;
    $[67] = retryLimitExceeded;
    $[68] = send;
    $[69] = state;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] !== state) {
    t17 = state.matches("configInvalid") ? <ConfigInvalid /> : null;
    $[71] = state;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  let t18;
  if ($[73] !== t10 || $[74] !== t11 || $[75] !== t12 || $[76] !== t13 || $[77] !== t14 || $[78] !== t15 || $[79] !== t16 || $[80] !== t17) {
    t18 = <AppErrorBoundary onReset={t10}>{t11}{t12}{t13}{t14}{t15}{t16}{t17}</AppErrorBoundary>;
    $[73] = t10;
    $[74] = t11;
    $[75] = t12;
    $[76] = t13;
    $[77] = t14;
    $[78] = t15;
    $[79] = t16;
    $[80] = t17;
    $[81] = t18;
  } else {
    t18 = $[81];
  }
  return t18;
};
export default Router;
function _temp(error) {
  logWarn(`Validating user session failed with error: ${getErrorMessage(error)}`, error);
}